import { useState, type ReactNode } from 'react';
import { Tabs as MantineTabs, Box } from '@mantine/core';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';

export interface TabViewItem {
  /** Unique identifier for this tab */
  value: string;
  /** Label shown in the tab bar */
  title: ReactNode;
  /** Content rendered below the tab bar when this tab is active */
  content: ReactNode;
}

export interface TabViewProps extends CoreProps {
  tabs: TabViewItem[];
  /** Initially active tab value (uncontrolled). Defaults to first tab. */
  defaultValue?: string;
  /** Controlled active tab value */
  value?: string;
  /** Called whenever the active tab changes */
  onChange?: (value: string) => void;
  /** Mantine responsive breakpoint — hide below this breakpoint */
  visibleFrom?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Mantine responsive breakpoint — hide above this breakpoint */
  hiddenFrom?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const LIST_STYLES = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  gap: 4,
  border: 'none',
  backgroundColor: 'var(--mantine-color-gray-1)',
  borderRadius: 'var(--mantine-radius-md)',
  padding: 0,
  minHeight: 40,
  height: 'auto',
} as const;

const TAB_STYLES = {
  flex: '1 1 auto',
  minHeight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--mantine-radius-sm)',
  border: 'none',
  fontWeight: 500,
  padding: '6px 12px',
  cursor: 'pointer',
  color: 'var(--mantine-color-dimmed)',
  backgroundColor: 'transparent',
  transition: 'background-color 150ms ease, color 150ms ease',
  lineHeight: 1.2,
  whiteSpace: 'nowrap' as const,
} as const;

const TAB_ACTIVE_STYLES = {
  backgroundColor: 'var(--mantine-primary-color-filled)',
  color: 'white',
} as const;

export function TabView({
  tabs,
  defaultValue,
  value: controlledValue,
  onChange,
  visibleFrom,
  hiddenFrom,
  testId,
  view = 'all',
}: TabViewProps) {
  const shouldRender = useShouldRender(view);
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue ?? tabs[0]?.value ?? '',
  );

  if (!shouldRender || !tabs.length) return null;

  const isControlled = controlledValue !== undefined;
  const activeValue = isControlled ? controlledValue : internalValue;

  function handleChange(val: string | null) {
    if (!val) return;
    if (!isControlled) setInternalValue(val);
    onChange?.(val);
  }

  const activeContent = tabs.find((t) => t.value === activeValue)?.content;

  return (
    <Box visibleFrom={visibleFrom} hiddenFrom={hiddenFrom} data-testid={testId}>
      <MantineTabs
        value={activeValue}
        onChange={handleChange}
        variant="unstyled"
        styles={{ list: LIST_STYLES }}
      >
        <MantineTabs.List>
          {tabs.map((tab) => {
            const isActive = tab.value === activeValue;
            return (
              <MantineTabs.Tab
                key={tab.value}
                value={tab.value}
                style={{
                  ...TAB_STYLES,
                  ...(isActive ? TAB_ACTIVE_STYLES : {}),
                }}
              >
                {tab.title}
              </MantineTabs.Tab>
            );
          })}
        </MantineTabs.List>
      </MantineTabs>

      {activeContent != null ? (
        <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
          {activeContent}
        </div>
      ) : null}
    </Box>
  );
}
